/**
 * Módulo centralizado para validação e formatação de CPF/CNPJ
 * Suporta CNPJ alfanumérico conforme nova regra da Receita Federal
 */

/**
 * Remove pontuação mantendo letras e números
 * @param {String} value
 * @returns {String}
 */
export function cleanValue(value) {
  if (!value) return '';
  return value.replace(/[.\-/\s]/g, '');
}

/**
 * Remove tudo exceto números
 * @param {String} value
 * @returns {String}
 */
export function cleanNumeric(value) {
  if (!value) return '';
  return value.replace(/\D/g, '');
}

/**
 * Remove tudo exceto letras e números
 * @param {String} value
 * @returns {String}
 */
export function cleanAlphanumeric(value) {
  if (!value) return '';
  return value.replace(/[^a-zA-Z0-9]/g, '');
}

/**
 * Verifica se a string contém apenas números
 * @param {String} value
 * @returns {Boolean}
 */
export function isNumeric(value) {
  return /^\d+$/.test(value);
}

/**
 * Converte caractere alfanumérico para valor numérico (0-9 → 0-9, A-Z → 10-35)
 * @param {String} char
 * @returns {Number}
 */
function charToValue(char) {
  return char.toUpperCase().charCodeAt(0) - 48;
}

/**
 * Verifica se todos os caracteres são iguais
 * @param {String} value
 * @returns {Boolean}
 */
export function hasAllSameCharacters(value) {
  if (!value || value.length === 0) return false;
  return value.split('').every(char => char.toUpperCase() === value[0].toUpperCase());
}

/**
 * Valida CPF (apenas numérico)
 * @param {String} cpf - CPF com ou sem formatação
 * @returns {Boolean}
 */
export function validateCPF(cpf) {
  const cleanedCpf = cleanNumeric(cpf);
  
  if (!cleanedCpf || cleanedCpf.length !== 11) {
    return false;
  }

  // Rejeita CPFs conhecidos como inválidos
  const invalidCpfs = [
    '00000000000', '11111111111', '22222222222', '33333333333',
    '44444444444', '55555555555', '66666666666', '77777777777',
    '88888888888', '99999999999'
  ];
  
  if (invalidCpfs.includes(cleanedCpf)) {
    return false;
  }

  // Validação do primeiro dígito verificador
  let soma = 0;
  for (let i = 1; i <= 9; i++) {
    soma += parseInt(cleanedCpf.substring(i - 1, i)) * (11 - i);
  }
  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cleanedCpf.substring(9, 10))) {
    return false;
  }

  // Validação do segundo dígito verificador
  soma = 0;
  for (let i = 1; i <= 10; i++) {
    soma += parseInt(cleanedCpf.substring(i - 1, i)) * (12 - i);
  }
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cleanedCpf.substring(10, 11))) {
    return false;
  }

  return true;
}

/**
 * Valida CNPJ (numérico com dígitos verificadores ou alfanumérico)
 * @param {String} cnpj - CNPJ com ou sem formatação
 * @returns {Boolean}
 */
export function validateCNPJ(cnpj) {
  const cleanedCnpj = cleanAlphanumeric(cnpj);
  
  if (!cleanedCnpj || cleanedCnpj.length !== 14) {
    return false;
  }

  // Valida formato alfanumérico
  if (!/^[A-Za-z0-9]{14}$/.test(cleanedCnpj)) {
    return false;
  }
  
  // Rejeita se todos caracteres forem iguais
  if (hasAllSameCharacters(cleanedCnpj)) {
    return false;
  }

  // Valida dígitos verificadores (algoritmo mod 11, válido para numérico e alfanumérico)
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  let soma = 0;
  for (let i = 0; i < 12; i++) {
    soma += charToValue(cleanedCnpj[i]) * weights1[i];
  }
  let resto = soma % 11;
  let d1 = resto < 2 ? 0 : 11 - resto;
  if (d1 !== parseInt(cleanedCnpj[12])) {
    return false;
  }

  soma = 0;
  for (let i = 0; i < 13; i++) {
    soma += charToValue(cleanedCnpj[i]) * weights2[i];
  }
  resto = soma % 11;
  let d2 = resto < 2 ? 0 : 11 - resto;
  if (d2 !== parseInt(cleanedCnpj[13])) {
    return false;
  }

  return true;
}

/**
 * Valida CPF ou CNPJ
 * @param {String} value - CPF/CNPJ com ou sem formatação
 * @returns {Boolean}
 */
export function validateCpfCnpj(value) {
  const cleanedValue = cleanAlphanumeric(value);
  
  if (cleanedValue.length === 11) {
    return validateCPF(value);
  } else if (cleanedValue.length === 14) {
    return validateCNPJ(value);
  }
  
  return false;
}

/**
 * Formata CPF para XXX.XXX.XXX-XX
 * @param {String} cpf
 * @returns {String}
 */
export function formatCPF(cpf) {
  const cleaned = cleanNumeric(cpf);
  if (cleaned.length !== 11) return cpf;
  return cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, '$1.$2.$3-$4');
}

/**
 * Formata CNPJ para XX.XXX.XXX/XXXX-XX (aceita alfanumérico)
 * @param {String} cnpj
 * @returns {String}
 */
export function formatCNPJ(cnpj) {
  const cleaned = cleanAlphanumeric(cnpj);
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(/^([A-Za-z0-9]{2})([A-Za-z0-9]{3})([A-Za-z0-9]{3})([A-Za-z0-9]{4})([A-Za-z0-9]{2}).*/, '$1.$2.$3/$4-$5');
}

/**
 * Formata CPF ou CNPJ automaticamente
 * @param {String} value
 * @returns {String}
 */
export function formatCpfCnpj(value) {
  if (!value) return '';
  
  const cleaned = cleanAlphanumeric(value);
  
  if (cleaned.length <= 11) {
    // CPF: Remove letras e formata
    const numericOnly = cleanNumeric(cleaned);
    return formatCPF(numericOnly);
  } else {
    // CNPJ: Mantém alfanumérico e formata
    return formatCNPJ(cleaned);
  }
}

/**
 * Detecta o tipo de documento
 * @param {String} value
 * @returns {String} 'cpf' | 'cnpj' | 'unknown'
 */
export function detectDocumentType(value) {
  const cleaned = cleanAlphanumeric(value);
  
  if (cleaned.length === 11) return 'cpf';
  if (cleaned.length === 14) return 'cnpj';
  
  return 'unknown';
}

export default {
  cleanValue,
  cleanNumeric,
  cleanAlphanumeric,
  isNumeric,
  hasAllSameCharacters,
  validateCPF,
  validateCNPJ,
  validateCpfCnpj,
  formatCPF,
  formatCNPJ,
  formatCpfCnpj,
  detectDocumentType
};
